import type { CourseSchema } from "@repo/validators";
import type { z } from "zod";
import { useCallback } from "react";
import { Permissions } from "@repo/database";
import { capitalize } from "@repo/utils";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { Course } from "~/providers/course-provider";
import type { ManagerResult } from "~/types";
import { api } from "~/lib/api";
import { useOrderChartStore } from "~/stores/use-order-chart-store";
import { usePermissionManager } from "./use-permission-manager";

export const useOrderChartManager = () => {
  const { t } = useTranslation("common");

  const { handleOrderChartUpdate } = useOrderChartUpdates();

  const { mutate: createOrderChart } = api.course.createCourse.useMutation({
    async onSuccess(result) {
      toast.success(
        capitalize(
          t("create_item_success", { item: t("OrderChart.order_chart") }),
        ),
      );
      await handleOrderChartUpdate(result);
    },
    onError(e) {
      if (e.data?.code === "CONFLICT") {
        toast.error(t("OrderChart.position_taken"), {
          duration: 5000,
        });
      } else {
        toast.error("Could not create order chart", {
          duration: 5000,
        });
      }
    },
  });

  const { mutate: updateOrderChart } = api.course.updateCourse.useMutation({
    async onSuccess(_, variables) {
      toast.success(
        capitalize(t("update_item_success", { item: "order chart" })),
      );
      await handleOrderChartUpdate(variables.id);
    },
    onError(e) {
      if (e.data?.code === "CONFLICT") {
        toast.error("Position already in use", {
          duration: 5000,
        });
      } else {
        toast.error("Could not update order chart", {
          duration: 5000,
        });
      }
    },
  });

  const { hasPermission } = usePermissionManager();

  const { setOrderChartFormOpen, setSelectedOrderChart } = useOrderChartStore();

  const openOrderChartDialogHandler = async (orderChart?: Course) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_order_chart,
      label: t("Permissions.create_course_label"),
    });

    if (!permission) return null;
    setSelectedOrderChart(orderChart);
    setOrderChartFormOpen(true);
  };

  const createOrderChartHandler = async (
    course: z.infer<typeof CourseSchema>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_order_chart,
      label: t("Permissions.create_course_label"),
    });

    return new Promise<ManagerResult<keyof z.infer<typeof CourseSchema>>>(
      (resolve) => {
        if (!permission) {
          return resolve({
            error: { path: "root", message: t("Permissions.no_permission") },
          });
        }
        createOrderChart(course, {
          onSuccess() {
            return resolve({
              data: true,
            });
          },
          onError(e) {
            if (e.data?.code === "CONFLICT") {
              return resolve({
                error: {
                  path: "position",
                  message: t("OrderChart.position_taken"),
                },
              });
            }

            return resolve({
              error: {
                path: "root",
                message: t("create_item_error", {
                  item: t("OrderChart.order_chart"),
                }),
              },
            });
          },
        });
      },
    );
  };

  const updateOrderChartHandler = async (
    id: string,
    course: z.infer<typeof CourseSchema>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_order_chart,
      label: t("Permissions.create_course_label"),
    });

    if (!permission) return false;

    return await new Promise<boolean>((resolve) => {
      updateOrderChart(
        {
          ...course,
          id,
        },
        {
          onSuccess() {
            resolve(true);
          },
          onError() {
            resolve(false);
          },
        },
      );
    });
  };

  return {
    openOrderChartDialog: openOrderChartDialogHandler,
    createOrderChart: createOrderChartHandler,
    updateOrderChart: updateOrderChartHandler,
  };
};

export const useOrderChartUpdates = () => {
  const utils = api.useUtils();

  const handleOrderChartUpdate = useCallback(
    async (orderChartId: string) => {
      const result = await utils.course.getById.fetch(orderChartId);

      void utils.course.getAll.setData(undefined, (old) => {
        if (!old) return old;
        return [...old.filter((p) => p.id !== result.id), result];
      });
    },
    [utils.course.getAll, utils.course.getById],
  );

  const handleOrderChartDelete = useCallback(
    (orderChartId: string) => {
      void utils.course.getAll.setData(undefined, (old) => {
        if (!old) return old;
        return old.filter((p) => p.id !== orderChartId);
      });
    },
    [utils.course.getAll],
  );

  return {
    handleOrderChartUpdate,
    handleOrderChartDelete,
  };
};
